import { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Phone,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Bell,
  Filter,
  Edit,
  Trash2,
  PawPrint,
} from 'lucide-react';
import {
  useAppointments,
  type AppointmentData,
} from '../../context/AppointmentsContext';
import { usePets } from '../../context/PetsContext';
import {
  useReminders,
  type ReminderCategory,
  type Reminder,
} from '../../context/RemindersContext';
import { useTheme } from '../../context/ThemeContext';
import {
  useCalendarRecordEvents,
  type RecordCalendarEvent,
  type RecordEventType,
} from '../../../../hooks/useCalendarRecordEvents';
import { BookingFlow } from '../appointments/BookingFlow';
import { CreateReminderModal } from '../shared/CreateReminderModal';
import { ReminderCard } from '../shared/ReminderCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

type ViewType = 'day' | 'week' | 'month' | 'agenda';

const WEEKDAY_KEYS = [
  'weekdaySun',
  'weekdayMon',
  'weekdayTue',
  'weekdayWed',
  'weekdayThu',
  'weekdayFri',
  'weekdaySat',
] as const;

function getDateLocale(i18nLanguage: string): string {
  const lang = (i18nLanguage || 'en').split('-')[0].toLowerCase();
  if (lang === 'es') return 'es';
  if (lang === 'pl') return 'pl';
  if (lang === 'it') return 'it';
  if (lang === 'fr') return 'fr';
  if (lang === 'de') return 'de';
  if (lang === 'nl') return 'nl';
  return 'en';
}

export function OwnerCalendar() {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const dateLocale = getDateLocale(
    i18n.language || i18n.resolvedLanguage || 'en'
  );

  const categoryFilterOptions: {
    value: ReminderCategory | 'all';
    label: string;
    icon: string;
  }[] = [
    { value: 'all', label: t('calendar.allCategories'), icon: '' },
    { value: 'vaccination', label: t('calendar.vaccination'), icon: '💉' },
    { value: 'health-checkup', label: t('calendar.healthCheckup'), icon: '🏥' },
    { value: 'medication', label: t('calendar.medication'), icon: '💊' },
    { value: 'grooming', label: t('calendar.grooming'), icon: '✂️' },
    { value: 'follow-up', label: t('calendar.followUp'), icon: '📋' },
    {
      value: 'document-renewal',
      label: t('calendar.documentRenewal'),
      icon: '📄',
    },
    { value: 'custom', label: t('calendar.custom'), icon: '📌' },
  ];

  const statusConfig = {
    scheduled: {
      color: 'bg-blue-100 border-blue-400 text-blue-700',
      icon: CalendarIcon,
      label: t('calendar.scheduled'),
    },
    confirmed: {
      color: 'bg-green-100 border-green-400 text-green-700',
      icon: CheckCircle,
      label: t('calendar.confirmed'),
    },
    completed: {
      color: 'bg-gray-100 border-gray-400 text-gray-700',
      icon: CheckCircle,
      label: t('calendar.completed'),
    },
    cancelled: {
      color: 'bg-red-100 border-red-400 text-red-700',
      icon: XCircle,
      label: t('calendar.cancelled'),
    },
    'pending-payment': {
      color: 'bg-yellow-100 border-yellow-400 text-yellow-700',
      icon: AlertCircle,
      label: t('calendar.pendingPayment'),
    },
  };
  const { appointments } = useAppointments();
  const { pets } = usePets();
  const { reminders, deleteReminder } = useReminders();
  const { recordEvents } = useCalendarRecordEvents(
    pets.map((p) => ({ id: p.id, name: p.name, species: p.species }))
  );
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>('month');
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentData | null>(null);
  const [showBookingFlow, setShowBookingFlow] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showBookingComingSoon, setShowBookingComingSoon] = useState(false);
  const [showCreateReminderModal, setShowCreateReminderModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(
    null
  );
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [selectedRecordEvent, setSelectedRecordEvent] =
    useState<RecordCalendarEvent | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<
    ReminderCategory | 'all'
  >('all');
  const [petFilter, setPetFilter] = useState<string>('all');

  const recordEventTypeToCategory: Record<RecordEventType, ReminderCategory> = {
    'vaccination-administered': 'vaccination',
    'vaccination-expiry': 'vaccination',
    'vaccination-booster': 'vaccination',
    'medical-update': 'health-checkup',
    'medical-followup': 'follow-up',
    grooming: 'grooming',
    'insurance-travel-expiry': 'document-renewal',
    'insurance-health-expiry': 'document-renewal',
  };

  const filteredAppointments =
    petFilter === 'all'
      ? appointments
      : appointments.filter((a) => a.petId === petFilter);

  const filteredRecordEvents = (
    categoryFilter === 'all'
      ? recordEvents
      : recordEvents.filter(
          (e) => recordEventTypeToCategory[e.type] === categoryFilter
        )
  ).filter((e) => petFilter === 'all' || e.petId === petFilter);

  const filteredReminders = (
    categoryFilter === 'all'
      ? reminders
      : reminders.filter((r) => r.category === categoryFilter)
  ).filter((r) => petFilter === 'all' || r.petId === petFilter);

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewType === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  // Get week days
  const getWeekDays = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // Get month days
  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add previous month's trailing days
    for (let i = 0; i < startingDayOfWeek; i++) {
      const day = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push({ date: day, isCurrentMonth: false });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  // Filter appointments by date range
  const filterAppointmentsByDate = (startDate: Date, endDate: Date) => {
    return filteredAppointments.filter((apt) => {
      const aptDate = new Date(apt.date);
      return aptDate >= startDate && aptDate <= endDate;
    });
  };

  // Get appointments for a specific day
  const getAppointmentsForDay = (date: Date) => {
    return filteredAppointments
      .filter((apt) => {
        const aptDate = new Date(apt.date);
        return aptDate.toDateString() === date.toDateString();
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  // Get reminders for a specific day (uses filteredReminders so calendar respects category filter)
  const getRemindersForDay = (date: Date) => {
    return filteredReminders
      .filter((reminder) => {
        // Skip completed reminders
        if (reminder.status === 'completed') return false;

        const reminderDate = new Date(reminder.date + 'T' + reminder.time);
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        const originalDate = new Date(reminderDate);
        originalDate.setHours(0, 0, 0, 0);

        // Check if it's the original date
        if (checkDate.toDateString() === originalDate.toDateString()) {
          return true;
        }

        // If it's before the original date, don't show
        if (checkDate < originalDate) {
          return false;
        }

        // Handle repeat logic
        if (reminder.repeat === 'daily') {
          return checkDate >= originalDate;
        } else if (reminder.repeat === 'weekly') {
          const daysDiff = Math.floor(
            (checkDate.getTime() - originalDate.getTime()) /
              (1000 * 60 * 60 * 24)
          );
          return daysDiff % 7 === 0;
        } else if (reminder.repeat === 'monthly') {
          // Check if it's the same day of the month in a future month
          return (
            checkDate.getDate() === originalDate.getDate() &&
            checkDate > originalDate
          );
        } else if (reminder.repeat === 'custom' && reminder.customRepeatDays) {
          const daysDiff = Math.floor(
            (checkDate.getTime() - originalDate.getTime()) /
              (1000 * 60 * 60 * 24)
          );
          return daysDiff > 0 && daysDiff % reminder.customRepeatDays === 0;
        }

        return false;
      })
      .sort((a, b) => {
        const timeA = new Date(a.date + 'T' + a.time).getTime();
        const timeB = new Date(b.date + 'T' + b.time).getTime();
        return timeA - timeB;
      });
  };

  // Format date header
  const getDateHeader = () => {
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      year: 'numeric',
    };
    if (viewType === 'day') {
      return currentDate.toLocaleDateString(dateLocale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return currentDate.toLocaleDateString(dateLocale, options);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(dateLocale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getPetEmoji = (species: string) => {
    return species === 'dog' ? '🐕' : '🐈';
  };

  // Get pet information from petId
  const getPetFromId = (petId?: string) => {
    if (!petId) return null;
    return pets.find((p) => p.id === petId);
  };

  const getRecordEventIcon = (type: RecordEventType) => {
    const icons: Record<RecordEventType, string> = {
      'vaccination-administered': '💉',
      'vaccination-expiry': '💉',
      'vaccination-booster': '💉',
      'medical-update': '🏥',
      'medical-followup': '📋',
      grooming: '✂️',
      'insurance-travel-expiry': '🛡️',
      'insurance-health-expiry': '🛡️',
    };
    return icons[type] || '📌';
  };

  const hasNotifiedRef = useRef(false);
  useEffect(() => {
    if (hasNotifiedRef.current) return;
    const overdue = recordEvents.filter((e) => e.isOverdue);
    const dueSoon = recordEvents.filter((e) => e.isDue && !e.isOverdue);
    if (overdue.length > 0) {
      hasNotifiedRef.current = true;
      toast.warning(
        t('calendar.overdueRecordsNotice', { count: overdue.length }),
        {
          duration: 6000,
        }
      );
    } else if (dueSoon.length > 0) {
      hasNotifiedRef.current = true;
      toast.info(
        t('calendar.dueSoonRecordsNotice', { count: dueSoon.length }),
        {
          duration: 5000,
        }
      );
    }
  }, [recordEvents]);

  const getRecordEventsForDayFiltered = (date: Date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return filteredRecordEvents
      .filter((e) => {
        const eventDate = new Date(e.date + 'T' + (e.time || '09:00'));
        eventDate.setHours(0, 0, 0, 0);
        return checkDate.toDateString() === eventDate.toDateString();
      })
      .sort((a, b) => {
        const timeA = (a.time || '09:00').replace(':', '');
        const timeB = (b.time || '09:00').replace(':', '');
        return parseInt(timeA, 10) - parseInt(timeB, 10);
      });
  };

  // Get reminder category icon
  const getReminderIcon = (category: string) => {
    const icons: Record<string, string> = {
      vaccination: '💉',
      'health-checkup': '🏥',
      medication: '💊',
      grooming: '✂️',
      'follow-up': '📋',
      'document-renewal': '📄',
      custom: '🔔',
    };
    return icons[category] || '🔔';
  };

  const handleCancelAppointment = (_appointmentId: string) => {
    toast.success(t('calendar.appointmentCancelledSuccess'));
    setSelectedAppointment(null);
  };

  // Render Day View
  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDay(currentDate);
    const dayReminders = getRemindersForDay(currentDate);
    const dayRecordEvents = getRecordEventsForDayFiltered(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: colors.border }}
      >
        <div className="grid grid-cols-[80px_1fr]">
          {/* Time column */}
          <div style={{ backgroundColor: colors.surfaceHover }}>
            <div
              className="h-12 border-b"
              style={{ borderColor: colors.border }}
            ></div>
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-16 border-b text-sm px-2 py-1"
                style={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                }}
              >
                {hour === 0
                  ? '12 AM'
                  : hour < 12
                    ? `${hour} AM`
                    : hour === 12
                      ? '12 PM'
                      : `${hour - 12} PM`}
              </div>
            ))}
          </div>

          {/* Appointments column */}
          <div className="relative">
            <div
              className="h-12 border-b px-4 flex items-center"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.surfaceHover,
              }}
            >
              <span style={{ color: colors.text }}>
                {currentDate.toLocaleDateString(dateLocale, {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
            <div className="relative">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="h-16 border-b"
                  style={{ borderColor: colors.border }}
                ></div>
              ))}
              {dayAppointments.map((apt) => {
                const aptDate = new Date(apt.date);
                const hour = aptDate.getHours();
                const minutes = aptDate.getMinutes();
                const top = hour * 64 + (minutes / 60) * 64;
                const height = (apt.duration / 60) * 64;
                const config =
                  statusConfig[apt.status as keyof typeof statusConfig];

                return (
                  <button
                    key={apt.id}
                    onClick={() => setSelectedAppointment(apt)}
                    className={`absolute left-2 right-2 ${config.color} border-l-4 rounded px-2 py-1 text-left text-sm overflow-hidden hover:shadow-md transition-shadow`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                  >
                    <div className="font-medium">
                      {getPetEmoji(apt.petSpecies)} {apt.petName}
                    </div>
                    <div className="text-xs">{apt.service}</div>
                    <div className="text-xs opacity-75">
                      {formatTime(aptDate)}
                    </div>
                  </button>
                );
              })}
              {dayReminders.map((reminder) => {
                const reminderDate = new Date(
                  reminder.date + 'T' + reminder.time
                );
                const hour = reminderDate.getHours();
                const minutes = reminderDate.getMinutes();
                const top = hour * 64 + (minutes / 60) * 64;
                const height = 20;
                const pet = getPetFromId(reminder.petId);

                return (
                  <button
                    type="button"
                    key={reminder.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedReminder(reminder);
                    }}
                    className="absolute left-1 right-1 bg-indigo-50 border-l-4 border-indigo-400 rounded px-1 py-0.5 text-left text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                    style={{ top: `${top}px`, height: `${height}px` }}
                  >
                    <div className="font-medium truncate text-indigo-900">
                      {pet
                        ? getPetEmoji(pet.species)
                        : getReminderIcon(reminder.category)}{' '}
                      {reminder.title}
                    </div>
                  </button>
                );
              })}
              {dayRecordEvents.map((evt) => {
                const [h, m] = (evt.time || '09:00').split(':').map(Number);
                const top = h * 64 + (m / 60) * 64;
                const height = 20;
                return (
                  <button
                    type="button"
                    key={evt.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedRecordEvent(evt);
                    }}
                    className={`absolute left-1 right-1 rounded px-1 py-0.5 text-left text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
                      evt.isOverdue
                        ? 'bg-red-50 border-red-400 text-red-900'
                        : evt.isDue
                          ? 'bg-amber-50 border-amber-400 text-amber-900'
                          : 'bg-emerald-50 border-emerald-400 text-emerald-900'
                    }`}
                    style={{ top: `${top}px`, height: `${height}px` }}
                  >
                    <div className="font-medium truncate">
                      {getPetEmoji(evt.petSpecies || 'dog')}{' '}
                      {getRecordEventIcon(evt.type)} {evt.title}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div
        className="border rounded-lg overflow-hidden"
        style={{ borderColor: colors.border }}
      >
        <div className="grid grid-cols-[80px_repeat(7,1fr)]">
          {/* Time column header */}
          <div
            style={{ backgroundColor: colors.surfaceHover }}
            className="border-r"
          />

          {/* Day headers */}
          {weekDays.map((day, i) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div
                key={i}
                className="h-12 border-b border-r px-2 py-1 text-center"
                style={{
                  borderColor: colors.border,
                  backgroundColor: isToday
                    ? colors.primary
                    : colors.surfaceHover,
                  color: isToday ? '#FFFFFF' : colors.text,
                }}
              >
                <div className="text-xs opacity-75">
                  {day.toLocaleDateString(dateLocale, { weekday: 'short' })}
                </div>
                <div className="font-medium">{day.getDate()}</div>
              </div>
            );
          })}

          {/* Time labels */}
          <div
            style={{ backgroundColor: colors.surfaceHover }}
            className="border-r"
          >
            {hours.map((hour) => (
              <div
                key={hour}
                className="h-16 border-b text-xs px-2 py-1"
                style={{
                  borderColor: colors.border,
                  color: colors.textSecondary,
                }}
              >
                {hour === 0
                  ? '12a'
                  : hour < 12
                    ? `${hour}a`
                    : hour === 12
                      ? '12p'
                      : `${hour - 12}p`}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIndex) => {
            const dayAppointments = getAppointmentsForDay(day);
            const dayReminders = getRemindersForDay(day);
            const dayRecordEvents = getRecordEventsForDayFiltered(day);
            return (
              <div
                key={dayIndex}
                className="relative border-r"
                style={{ borderColor: colors.border }}
              >
                {hours.map((hour) => (
                  <div
                    key={hour}
                    className="h-16 border-b"
                    style={{ borderColor: colors.border }}
                  ></div>
                ))}
                {dayAppointments.map((apt) => {
                  const aptDate = new Date(apt.date);
                  const hour = aptDate.getHours();
                  const minutes = aptDate.getMinutes();
                  const top = hour * 64 + (minutes / 60) * 64;
                  const height = Math.max((apt.duration / 60) * 64, 20);
                  const config =
                    statusConfig[apt.status as keyof typeof statusConfig];

                  return (
                    <button
                      key={apt.id}
                      onClick={() => setSelectedAppointment(apt)}
                      className={`absolute left-1 right-1 ${config.color} border-l-4 rounded px-1 py-0.5 text-left text-xs overflow-hidden hover:shadow-md transition-shadow`}
                      style={{ top: `${top}px`, height: `${height}px` }}
                    >
                      <div className="font-medium truncate">
                        {getPetEmoji(apt.petSpecies)} {apt.petName}
                      </div>
                      <div className="truncate">{apt.service}</div>
                    </button>
                  );
                })}
                {dayReminders.map((reminder) => {
                  const reminderDate = new Date(
                    reminder.date + 'T' + reminder.time
                  );
                  const hour = reminderDate.getHours();
                  const minutes = reminderDate.getMinutes();
                  const top = hour * 64 + (minutes / 60) * 64;
                  const height = 20;
                  const pet = getPetFromId(reminder.petId);

                  return (
                    <button
                      type="button"
                      key={reminder.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedReminder(reminder);
                      }}
                      className="absolute left-1 right-1 bg-indigo-50 border-l-4 border-indigo-400 rounded px-1 py-0.5 text-left text-xs overflow-hidden hover:shadow-md transition-shadow"
                      style={{ top: `${top}px`, height: `${height}px` }}
                    >
                      <div className="font-medium truncate text-indigo-900">
                        {pet
                          ? getPetEmoji(pet.species)
                          : getReminderIcon(reminder.category)}{' '}
                        {reminder.title}
                      </div>
                    </button>
                  );
                })}
                {dayRecordEvents.map((evt) => {
                  const [h, m] = (evt.time || '09:00').split(':').map(Number);
                  const top = h * 64 + (m / 60) * 64;
                  return (
                    <button
                      type="button"
                      key={evt.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRecordEvent(evt);
                      }}
                      className={`absolute left-1 right-1 rounded px-1 py-0.5 text-left text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow border-l-4 ${
                        evt.isOverdue
                          ? 'bg-red-50 border-red-400 text-red-900'
                          : evt.isDue
                            ? 'bg-amber-50 border-amber-400 text-amber-900'
                            : 'bg-emerald-50 border-emerald-400 text-emerald-900'
                      }`}
                      style={{ top: `${top}px`, height: 20 }}
                    >
                      <div className="font-medium truncate">
                        {getPetEmoji(evt.petSpecies || 'dog')}{' '}
                        {getRecordEventIcon(evt.type)} {evt.title}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const monthDays = getMonthDays(currentDate);
    const today = new Date();

    return (
      <div
        className="border rounded-lg overflow-hidden min-w-0"
        style={{ borderColor: colors.border }}
      >
        {/* Weekday headers */}
        <div
          className="grid grid-cols-7 border-b min-w-0"
          style={{
            borderColor: colors.border,
            backgroundColor: colors.surfaceHover,
          }}
        >
          {WEEKDAY_KEYS.map((key) => (
            <div
              key={key}
              className="text-center py-2 text-sm"
              style={{ color: colors.textSecondary }}
            >
              {t(`calendar.${key}`)}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 min-w-0">
          {monthDays.map(({ date, isCurrentMonth }, index) => {
            const dayAppointments = getAppointmentsForDay(date);
            const dayReminders = getRemindersForDay(date);
            const dayRecordEvents = getRecordEventsForDayFiltered(date);
            const isToday = date.toDateString() === today.toDateString();
            const isPast = date < today && !isToday;

            return (
              <button
                key={index}
                onClick={() => {
                  if (!isPast && isCurrentMonth) {
                    setShowBookingFlow(true);
                  }
                }}
                disabled={isPast || !isCurrentMonth}
                className="min-h-24 border-r border-b p-2 text-left hover:bg-opacity-80 transition-colors"
                style={{
                  borderColor: colors.border,
                  backgroundColor: isCurrentMonth
                    ? colors.surface
                    : colors.surfaceHover,
                  opacity: isPast ? 0.4 : isCurrentMonth ? 1 : 0.5,
                  cursor: isPast || !isCurrentMonth ? 'not-allowed' : 'pointer',
                }}
              >
                <div
                  className={`text-sm mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                    isToday ? 'bg-blue-600 text-white font-medium' : ''
                  }`}
                  style={{ color: isToday ? '#FFFFFF' : colors.text }}
                >
                  {date.getDate()}
                </div>
                <div className="space-y-1">
                  {dayAppointments.slice(0, 2).map((apt) => {
                    const config =
                      statusConfig[apt.status as keyof typeof statusConfig];
                    return (
                      <div
                        key={apt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAppointment(apt);
                        }}
                        className={`w-full ${config.color} text-xs px-1 py-0.5 rounded truncate text-left hover:shadow-sm transition-shadow cursor-pointer`}
                      >
                        {getPetEmoji(apt.petSpecies)} {apt.service}
                      </div>
                    );
                  })}
                  {dayReminders
                    .slice(0, 3 - dayAppointments.length)
                    .map((reminder) => {
                      const pet = getPetFromId(reminder.petId);
                      return (
                        <div
                          key={reminder.id}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReminder(reminder);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedReminder(reminder);
                            }
                          }}
                          className="w-full bg-indigo-50 border-l-2 border-indigo-400 text-indigo-900 text-xs px-1 py-0.5 rounded truncate text-left hover:shadow-sm transition-shadow cursor-pointer"
                        >
                          {pet
                            ? `${getPetEmoji(pet.species)} `
                            : `${getReminderIcon(reminder.category)} `}
                          {reminder.title}
                        </div>
                      );
                    })}
                  {dayRecordEvents
                    .slice(
                      0,
                      Math.max(
                        0,
                        3 -
                          dayAppointments.length -
                          Math.min(
                            dayReminders.length,
                            Math.max(0, 3 - dayAppointments.length)
                          )
                      )
                    )
                    .map((evt) => (
                      <div
                        key={evt.id}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRecordEvent(evt);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedRecordEvent(evt);
                          }
                        }}
                        className={`w-full text-xs px-1 py-0.5 rounded truncate text-left hover:shadow-sm transition-shadow cursor-pointer border-l-2 ${
                          evt.isOverdue
                            ? 'bg-red-50 border-red-400 text-red-900'
                            : evt.isDue
                              ? 'bg-amber-50 border-amber-400 text-amber-900'
                              : 'bg-emerald-50 border-emerald-400 text-emerald-900'
                        }`}
                      >
                        {getPetEmoji(evt.petSpecies || 'dog')}{' '}
                        {getRecordEventIcon(evt.type)} {evt.title}
                      </div>
                    ))}
                  {dayAppointments.length +
                    dayReminders.length +
                    dayRecordEvents.length >
                    3 && (
                    <div
                      className="text-xs"
                      style={{ color: colors.textSecondary }}
                    >
                      +
                      {dayAppointments.length +
                        dayReminders.length +
                        dayRecordEvents.length -
                        3}{' '}
                      {t('calendar.more')}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render Agenda View - appointments + record events (vaccination expiry, due dates, etc.)
  const renderAgendaView = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const upcomingAppointments = filterAppointmentsByDate(today, nextMonth)
      .filter((apt) => apt.status !== 'cancelled' && apt.status !== 'completed')
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const upcomingRecordEvents = filteredRecordEvents
      .filter((e) => {
        const d = new Date(e.date + 'T' + (e.time || '09:00'));
        return (d >= today && d <= nextMonth) || e.isOverdue;
      })
      .sort(
        (a, b) =>
          new Date(a.date + 'T' + (a.time || '09:00')).getTime() -
          new Date(b.date + 'T' + (b.time || '09:00')).getTime()
      );

    const hasAny =
      upcomingAppointments.length > 0 || upcomingRecordEvents.length > 0;

    if (!hasAny) {
      return (
        <div
          className="border rounded-lg overflow-hidden min-h-[200px] flex items-center justify-center"
          style={{
            borderColor: colors.border,
            backgroundColor: colors.surface,
          }}
        >
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            {t('calendar.noUpcomingItems')}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {upcomingAppointments.map((apt) => {
          const config = statusConfig[apt.status as keyof typeof statusConfig];
          const aptDate = new Date(apt.date);

          return (
            <button
              key={apt.id}
              onClick={() => setSelectedAppointment(apt)}
              className="w-full border rounded-lg p-4 hover:shadow-md transition-shadow text-left"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.surface,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">
                      {getPetEmoji(apt.petSpecies)}
                    </span>
                    <div>
                      <h3
                        className="font-medium"
                        style={{ color: colors.text }}
                      >
                        {apt.petName}
                      </h3>
                      <p
                        className="text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        {apt.service}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div
                      className="flex items-center gap-2"
                      style={{ color: colors.textSecondary }}
                    >
                      <CalendarIcon className="w-4 h-4" />
                      {aptDate.toLocaleDateString(dateLocale, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                    <div
                      className="flex items-center gap-2"
                      style={{ color: colors.textSecondary }}
                    >
                      <Clock className="w-4 h-4" />
                      {formatTime(aptDate)} ({apt.duration} min)
                    </div>
                    <div
                      className="flex items-center gap-2"
                      style={{ color: colors.textSecondary }}
                    >
                      <MapPin className="w-4 h-4" />
                      {apt.location}
                    </div>
                    <div
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${config.color}`}
                    >
                      <config.icon className="w-3 h-3" />
                      {config.label}
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
        {upcomingRecordEvents.map((evt) => {
          const evtDate = new Date(evt.date + 'T' + (evt.time || '09:00'));
          return (
            <button
              key={evt.id}
              onClick={() => setSelectedRecordEvent(evt)}
              className="w-full border rounded-lg p-4 hover:shadow-md transition-shadow text-left"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.surface,
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">
                      {getPetEmoji(evt.petSpecies || 'dog')}
                    </span>
                    <div>
                      <h3
                        className="font-medium"
                        style={{ color: colors.text }}
                      >
                        {evt.petName} – {evt.title}
                      </h3>
                      <p
                        className="text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        {evt.type.includes('vaccination')
                          ? t('calendar.vaccination')
                          : evt.type.includes('medical')
                            ? t('calendar.categoryHealthCheckup')
                            : evt.type.includes('grooming')
                              ? t('calendar.grooming')
                              : t('calendar.insurance')}{' '}
                        • {t('calendar.fromHealthRecords')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div
                      className="flex items-center gap-2"
                      style={{ color: colors.textSecondary }}
                    >
                      <CalendarIcon className="w-4 h-4" />
                      {evtDate.toLocaleDateString(dateLocale, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                    {(evt.isOverdue || evt.isDue) && (
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          evt.isOverdue
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {evt.isOverdue
                          ? t('calendar.overdue')
                          : t('calendar.dueSoon')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 min-w-0">
      <div className="mb-4 sm:mb-6">
        <h1
          className="text-2xl sm:text-3xl mb-2"
          style={{ color: colors.text }}
        >
          {t('calendar.title')}
        </h1>
        <p
          className="text-sm break-words"
          style={{ color: colors.textSecondary }}
        >
          {t('calendar.subtitleAppointments')}
        </p>
      </div>

      {/* Controls - on mobile: row 1 = view + nav, row 2 = Book + filters + Add Reminder so Book is never cut off */}
      <div className="flex flex-col gap-3 mb-4 sm:mb-6">
        <div className="flex flex-nowrap items-center gap-2 overflow-x-auto pb-1">
          {(['day', 'week', 'month', 'agenda'] as ViewType[]).map((view) => (
            <button
              key={view}
              onClick={() => setViewType(view)}
              className="px-3 py-2 sm:py-1.5 rounded-lg capitalize transition-colors text-sm shrink-0"
              style={{
                backgroundColor:
                  viewType === view ? colors.primary : colors.surface,
                color: viewType === view ? '#FFFFFF' : colors.text,
                border: `1px solid ${colors.border}`,
              }}
            >
              {t(`calendar.${view}`)}
            </button>
          ))}
          {viewType !== 'agenda' && (
            <>
              <button
                onClick={goToPrevious}
                className="p-1.5 rounded-lg hover:bg-opacity-80 transition-colors shrink-0 min-w-[2.25rem] ml-1"
                style={{ backgroundColor: colors.surfaceHover }}
              >
                <ChevronLeft
                  className="w-4 h-4"
                  style={{ color: colors.text }}
                />
              </button>
              <div
                className="px-2 py-1.5 text-sm font-medium shrink-0 whitespace-nowrap min-w-[6rem] sm:min-w-[7rem] text-center"
                style={{ color: colors.text }}
              >
                {getDateHeader()}
              </div>
              <button
                onClick={goToNext}
                className="p-1.5 rounded-lg hover:bg-opacity-80 transition-colors shrink-0 min-w-[2.25rem]"
                style={{ backgroundColor: colors.surfaceHover }}
              >
                <ChevronRight
                  className="w-4 h-4"
                  style={{ color: colors.text }}
                />
              </button>
              <button
                onClick={goToToday}
                className="px-3 py-2 sm:py-1.5 rounded-lg text-sm transition-colors shrink-0"
                style={{
                  backgroundColor: colors.surface,
                  color: colors.primary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {t('calendar.today')}
              </button>
            </>
          )}
        </div>

        {/* Row 2: Book Appointment + filters + Add Reminder */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative inline-block overflow-visible shrink-0 w-full sm:w-auto">
            <button
              onClick={() => setShowBookingFlow(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2.5 sm:py-1.5 rounded-lg transition-colors hover:opacity-90 text-sm whitespace-nowrap"
              style={{
                backgroundColor: colors.primary,
                color: '#FFFFFF',
              }}
            >
              <Plus className="w-5 h-5 shrink-0" />
              {t('calendar.bookAppointment')}
            </button>
          </div>
          <Select
            value={petFilter}
            onValueChange={(value) => setPetFilter(value)}
          >
            <SelectTrigger
              className="h-9 min-h-9 px-3 py-1.5 rounded-lg gap-1.5 w-full sm:w-[7rem] sm:min-w-[7rem] min-w-0 shrink-0 [&_span]:truncate [&_span]:max-w-full text-sm"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              <PawPrint
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: colors.textSecondary }}
              />
              <SelectValue placeholder={t('calendar.petPlaceholder')} />
            </SelectTrigger>
            <SelectContent
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
              }}
            >
              <SelectItem value="all" style={{ color: colors.text }}>
                {t('calendar.allPets')}
              </SelectItem>
              {pets.map((pet) => (
                <SelectItem
                  key={pet.id}
                  value={pet.id}
                  style={{ color: colors.text }}
                >
                  <span className="mr-2">{getPetEmoji(pet.species)}</span>
                  {pet.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={categoryFilter}
            onValueChange={(value) =>
              setCategoryFilter(value as ReminderCategory | 'all')
            }
          >
            <SelectTrigger
              className="h-9 min-h-9 px-3 py-1.5 rounded-lg gap-1.5 w-[5.5rem] min-w-[5.5rem] shrink-0 [&_span]:truncate [&_span]:max-w-full text-sm"
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
                color: colors.text,
              }}
            >
              <Filter
                className="w-3.5 h-3.5 shrink-0"
                style={{ color: colors.textSecondary }}
              />
              <SelectValue placeholder={t('calendar.categoryPlaceholder')} />
            </SelectTrigger>
            <SelectContent
              style={{
                backgroundColor: colors.surface,
                borderColor: colors.border,
              }}
            >
              {categoryFilterOptions.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  style={{ color: colors.text }}
                >
                  {opt.icon && <span className="mr-2">{opt.icon}</span>}
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setShowCreateReminderModal(true)}
            className="flex items-center justify-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg transition-colors text-sm shrink-0"
            style={{
              backgroundColor: '#6366F1',
              color: '#FFFFFF',
            }}
          >
            <Bell className="w-5 h-5" />
            {t('calendar.addReminder')}
          </button>
        </div>
      </div>

      {/* Calendar Views */}
      {viewType === 'day' && renderDayView()}
      {viewType === 'week' && renderWeekView()}
      {viewType === 'month' && renderMonthView()}
      {viewType === 'agenda' && renderAgendaView()}

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: colors.surface }}
          >
            <div
              className="sticky top-0 flex items-center justify-between p-6 border-b"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.surface,
              }}
            >
              <h3 className="text-xl" style={{ color: colors.text }}>
                {t('calendar.appointmentDetails')}
              </h3>
              <button
                onClick={() => setSelectedAppointment(null)}
                className="p-2 hover:bg-opacity-80 rounded-lg transition-colors"
                style={{ backgroundColor: colors.surfaceHover }}
              >
                <X className="w-5 h-5" style={{ color: colors.text }} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Pet & Service Info */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">
                    {getPetEmoji(selectedAppointment.petSpecies)}
                  </span>
                  <div>
                    <h4 className="text-xl" style={{ color: colors.text }}>
                      {selectedAppointment.petName}
                    </h4>
                    <p style={{ color: colors.textSecondary }}>
                      {selectedAppointment.service}
                    </p>
                  </div>
                </div>

                {(() => {
                  const config =
                    statusConfig[
                      selectedAppointment.status as keyof typeof statusConfig
                    ];
                  return (
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.color}`}
                    >
                      <config.icon className="w-4 h-4" />
                      {config.label}
                    </div>
                  );
                })()}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div
                    className="flex items-center gap-2 mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    <CalendarIcon className="w-4 h-4" />
                    <span className="text-sm">{t('calendar.dateLabel')}</span>
                  </div>
                  <p style={{ color: colors.text }}>
                    {new Date(selectedAppointment.date).toLocaleDateString(
                      dateLocale,
                      {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      }
                    )}
                  </p>
                </div>
                <div>
                  <div
                    className="flex items-center gap-2 mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">
                      {t('calendar.createReminderModal.timeLabel')}
                    </span>
                  </div>
                  <p style={{ color: colors.text }}>
                    {formatTime(new Date(selectedAppointment.date))} (
                    {selectedAppointment.duration} {t('calendar.minutes')})
                  </p>
                </div>
              </div>

              {/* Professional & Location */}
              <div className="space-y-3">
                <div>
                  <p
                    className="text-sm mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.professional')}
                  </p>
                  <p style={{ color: colors.text }}>
                    {selectedAppointment.professionalName}
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.textSecondary }}
                  >
                    {selectedAppointment.professionalType}
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin
                    className="w-4 h-4 mt-1"
                    style={{ color: colors.textSecondary }}
                  />
                  <div>
                    <p
                      className="text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      {t('calendar.location')}
                    </p>
                    <p style={{ color: colors.text }}>
                      {selectedAppointment.location}
                    </p>
                  </div>
                </div>
                {selectedAppointment.professionalContact && (
                  <div className="flex items-center gap-2">
                    <Phone
                      className="w-4 h-4"
                      style={{ color: colors.textSecondary }}
                    />
                    <p style={{ color: colors.text }}>
                      {selectedAppointment.professionalContact}
                    </p>
                  </div>
                )}
              </div>

              {/* Notes */}
              {selectedAppointment.notes && (
                <div>
                  <p
                    className="text-sm mb-1"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.notes')}
                  </p>
                  <p style={{ color: colors.text }}>
                    {selectedAppointment.notes}
                  </p>
                </div>
              )}

              {/* Actions */}
              {selectedAppointment.status !== 'cancelled' &&
                selectedAppointment.status !== 'completed' && (
                  <div
                    className="flex gap-3 pt-4 border-t"
                    style={{ borderColor: colors.border }}
                  >
                    <button
                      onClick={() =>
                        handleCancelAppointment(selectedAppointment.id)
                      }
                      className="flex-1 px-4 py-2 border rounded-lg hover:bg-opacity-80 transition-colors"
                      style={{ borderColor: colors.border, color: '#EF4444' }}
                    >
                      {t('calendar.cancelAppointment')}
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Booking Coming Soon Modal - booking disabled until Petso Pro launch */}
      {showBookingComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowBookingComingSoon(false)}
            aria-hidden="true"
          />
          <div
            className="relative rounded-xl shadow-xl max-w-sm w-full p-6 text-center space-y-4"
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              borderWidth: 1,
            }}
          >
            <button
              type="button"
              onClick={() => setShowBookingComingSoon(false)}
              className="absolute right-3 top-3 p-1 rounded-md opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: colors.textSecondary }}
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
            <div
              className="text-2xl font-semibold"
              style={{ color: colors.text }}
            >
              {t('calendar.comingSoon')}
            </div>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              {t('calendar.bookingComingSoonDescription')}
            </p>
            <button
              type="button"
              onClick={() => setShowBookingComingSoon(false)}
              className="w-full py-2.5 px-4 rounded-lg font-medium transition-colors text-white"
              style={{ backgroundColor: colors.primary }}
            >
              {t('calendar.gotIt')}
            </button>
          </div>
        </div>
      )}

      {/* Booking Flow Modal - enabled when Petso Pro goes live */}
      {showBookingFlow && (
        <BookingFlow
          selectedDate={selectedDate}
          onClose={() => {
            setShowBookingFlow(false);
            setSelectedDate(null);
          }}
        />
      )}

      {/* Reminder Detail Modal - Edit / Delete */}
      {selectedReminder && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedReminder(null)}
          role="presentation"
        >
          <div
            className="rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              borderWidth: 1,
            }}
          >
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: colors.border }}
            >
              <h3
                className="text-lg font-semibold"
                style={{ color: colors.text }}
              >
                {t('calendar.reminderDetails')}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedReminder(null)}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: colors.surfaceHover,
                  color: colors.text,
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p
                  className="text-xs mb-0.5"
                  style={{ color: colors.textSecondary }}
                >
                  {t('calendar.titleLabel')}
                </p>
                <p className="font-medium" style={{ color: colors.text }}>
                  {selectedReminder.title}
                </p>
              </div>
              <div className="flex gap-4">
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    Date
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {new Date(selectedReminder.date).toLocaleDateString(
                      dateLocale,
                      {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }
                    )}
                  </p>
                </div>
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.createReminderModal.timeLabel')}
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {selectedReminder.time}
                  </p>
                </div>
              </div>
              {getPetFromId(selectedReminder.petId) && (
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.petLabel')}
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {getPetFromId(selectedReminder.petId)?.name}
                  </p>
                </div>
              )}
              {selectedReminder.notes && (
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.notes')}
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {selectedReminder.notes}
                  </p>
                </div>
              )}
            </div>
            <div
              className="flex gap-3 p-4 border-t"
              style={{ borderColor: colors.border }}
            >
              <button
                type="button"
                onClick={() => {
                  setEditingReminder(selectedReminder);
                  setSelectedReminder(null);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: colors.primary,
                  color: '#FFFFFF',
                }}
              >
                <Edit className="w-4 h-4" />
                {t('common.edit')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm(t('calendar.confirmDeleteReminder'))) {
                    deleteReminder(selectedReminder.id);
                    toast.success(t('calendar.reminderDeleted'));
                    setSelectedReminder(null);
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors"
                style={{
                  borderWidth: 1,
                  borderColor: colors.border,
                  color: '#EF4444',
                }}
              >
                <Trash2 className="w-4 h-4" />
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record Event Detail Modal - from Health & Care Records */}
      {selectedRecordEvent && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRecordEvent(null)}
          role="presentation"
        >
          <div
            className="rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: colors.surface,
              borderColor: colors.border,
              borderWidth: 1,
            }}
          >
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: colors.border }}
            >
              <h3
                className="text-lg font-semibold"
                style={{ color: colors.text }}
              >
                {getRecordEventIcon(selectedRecordEvent.type)}{' '}
                {t('calendar.record')}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedRecordEvent(null)}
                className="p-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: colors.surfaceHover,
                  color: colors.text,
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p
                  className="text-xs mb-0.5"
                  style={{ color: colors.textSecondary }}
                >
                  {t('calendar.fromHealthRecords')}
                </p>
                <p className="font-medium" style={{ color: colors.text }}>
                  {selectedRecordEvent.title}
                </p>
              </div>
              <div className="flex gap-4">
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.dateLabel')}
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {new Date(selectedRecordEvent.date).toLocaleDateString(
                      dateLocale,
                      {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      }
                    )}
                  </p>
                </div>
                <div>
                  <p
                    className="text-xs mb-0.5"
                    style={{ color: colors.textSecondary }}
                  >
                    {t('calendar.petLabel')}
                  </p>
                  <p className="text-sm" style={{ color: colors.text }}>
                    {selectedRecordEvent.petName}
                  </p>
                </div>
              </div>
              {(selectedRecordEvent.isOverdue || selectedRecordEvent.isDue) && (
                <div
                  className={`px-3 py-2 rounded-lg text-sm ${
                    selectedRecordEvent.isOverdue
                      ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                      : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                  }`}
                >
                  {selectedRecordEvent.isOverdue
                    ? t('calendar.overdueMessage')
                    : t('calendar.dueSoonMessage')}
                </div>
              )}
            </div>
            <div
              className="p-4 border-t"
              style={{ borderColor: colors.border }}
            >
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                {t('calendar.recordUpdateHint')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Reminder Modal */}
      {(showCreateReminderModal || editingReminder) && (
        <CreateReminderModal
          mode="petso"
          onClose={() => {
            setShowCreateReminderModal(false);
            setEditingReminder(null);
          }}
          editingReminder={editingReminder}
        />
      )}

      {/* Reminders (respects category filter) */}
      {filteredReminders.length > 0 && (
        <div className="mt-6">
          <h2
            className="text-xl font-medium mb-4"
            style={{ color: colors.text }}
          >
            {t('calendar.reminders')}
          </h2>
          <div className="space-y-4">
            {filteredReminders.map((reminder) => (
              <ReminderCard
                key={reminder.id}
                reminder={reminder}
                onClick={() => setSelectedReminder(reminder)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
